import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, Clock, HeartHandshake, HeartPulse, Home as HomeIcon, Palette, ShoppingBag, Sparkles, Stethoscope, Users, UtensilsCrossed, X } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { MORE_SERVICES, PRICING } from "@/data/content";

const MORE_ICONS = { Sparkles, HeartPulse, HomeIcon, Users, Palette, ShoppingBag, Building2, HeartHandshake, Stethoscope, Clock, UtensilsCrossed };

const MoreServices = () => (
  <section className="bg-white/50 px-4 pb-24 sm:px-6 lg:px-8" data-testid="more-services">
    <div className="mx-auto max-w-7xl">
      <SectionHeading
        eyebrow="And there's more"
        title="More ways we can help"
        text="Everything below can be booked on its own or folded into any monthly bundle. Tell us what would help — we'll shape a visit around it and confirm clear pricing at your free consultation."
      />
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {MORE_SERVICES.map((s, i) => {
          const Icon = MORE_ICONS[s.icon];
          return (
            <Reveal key={s.title} delay={(i % 5) * 0.06}>
              <article
                data-testid={`more-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="h-full rounded-3xl border border-sand bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-sage/40 hover:shadow-[0_18px_40px_rgba(28,56,41,0.1)]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage-tint text-sage">
                  <Icon className="h-4 w-4" />
                </span>
                <h3 className="mt-4 font-serif text-base text-forest">{s.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-pebble">{s.text}</p>
              </article>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-12 grid gap-10 rounded-[2.5rem] border border-sand bg-linen p-8 sm:p-10 md:grid-cols-2" data-testid="do-dont-panel">
          <div>
            <h3 className="flex items-center gap-2.5 font-serif text-xl text-forest">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sage-tint text-sage">
                <Check className="h-4 w-4" />
              </span>
              What Brightside does
            </h3>
            <ul className="mt-5 space-y-2.5 text-sm text-pebble">
              {[
                "Companionship, conversation and shared activities",
                "Outings, errands and appointment company",
                "Light housekeeping and mealtime company",
                "Wellbeing check-ins and family updates",
                "Respite company for family carers",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" /> {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="flex items-center gap-2.5 font-serif text-xl text-forest">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-terracotta/15 text-terracotta">
                <X className="h-4 w-4" />
              </span>
              What we don't do
            </h3>
            <ul className="mt-5 space-y-2.5 text-sm text-pebble">
              {[
                "Personal care — washing, dressing or toileting",
                "Medication or anything medical",
                "Nursing or clinical care",
                "Heavy cleaning or home maintenance",
                "Overnight or live-in care",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-terracotta/70" /> {item}
                </li>
              ))}
            </ul>
            <p className="mt-5 rounded-2xl bg-white/70 p-4 text-xs leading-relaxed text-pebble">
              Need personal or nursing care? We'll say so honestly and happily point you towards regulated care providers — many families use Brightside alongside formal care.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Services() {
  return (
    <>
      <Seo
        title="Our services — Brightside Befriending Services, Glasgow & Ayr"
        siteName="Brightside Befriending Services"
        description="Companionship visits, appointment escorts, telephone check-ins, bundles, hospital discharge support, light housekeeping, wellbeing visits, respite and care home befriending across Glasgow & Ayr. Free consultation with clear, tailored pricing."
      />
      <PageHero
        eyebrow="Our services"
        lines={["Good company,", "shaped around you."]}
        text="Every arrangement is tailored to the person, their location and what helps most — invoiced monthly in arrears, so you only ever pay for visits that have happened. We'll confirm clear, exact pricing at your free consultation."
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="pricing-grid-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PRICING.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`pricing-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`relative flex h-full flex-col rounded-3xl p-8 transition-transform duration-500 hover:-translate-y-1.5 ${
                    p.popular ? "bg-forest text-linen shadow-[0_24px_50px_rgba(28,56,41,0.3)]" : "border border-sand bg-white"
                  }`}
                >
                  {p.popular && (
                    <span className="absolute -top-3 left-8 rounded-full bg-terracotta px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-linen">Popular</span>
                  )}
                  <h3 className={`font-serif text-xl ${p.popular ? "text-linen" : "text-forest"}`}>{p.title}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${p.popular ? "text-linen/70" : "text-pebble"}`}>{p.description}</p>
                  <ul className="mt-6 flex-1 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className={`flex items-start gap-2.5 text-sm ${p.popular ? "text-linen/80" : "text-ink/80"}`}>
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-terracotta" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    data-testid={`pricing-book-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`mt-8 rounded-full py-5 ${
                      p.popular
                        ? "bg-terracotta text-linen hover:bg-terracotta-deep"
                        : "border border-forest/25 bg-transparent text-forest hover:bg-forest hover:text-linen"
                    }`}
                  >
                    <Link to="/contact">Enquire about this</Link>
                  </Button>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <MoreServices />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="b2b-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 rounded-[2.5rem] border border-sand bg-linen-deep p-8 sm:p-12 lg:grid-cols-[auto_1fr_auto]">
          <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-sage-tint text-sage">
            <Building2 className="h-7 w-7" />
          </span>
          <div>
            <h2 className="font-serif text-2xl text-forest sm:text-3xl">Care agency or care home?</h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-pebble sm:text-base">
              We partner with care providers who need extra companionship hours for their residents — reliable, PVG-checked befrienders without the recruitment overhead.
            </p>
          </div>
          <Button asChild data-testid="b2b-referrals-button" className="group rounded-full bg-forest px-7 py-6 text-linen hover:bg-terracotta">
            <Link to="/referrals">
              Partner with us
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </section>

      <CTASection title="Not sure which fits? Just ask." text="Tell us about the person you have in mind, and we'll suggest the kindest arrangement — even if that means less, not more." />
    </>
  );
}
